import { Award, BookOpen, MapPin, User, Users } from 'lucide-react';
import Hotspot from './Hotspot';

const DIMENSIONS = [
  { label: 'Identity', icon: User, value: 100, accent: '#5fa8d3' },
  { label: 'Memories', icon: BookOpen, value: 72, accent: '#e88a73' },
  { label: 'People', icon: Users, value: 58, accent: '#9a7bb8' },
  { label: 'Journeys', icon: MapPin, value: 84, accent: '#1d9e75' },
  { label: 'Recognition', icon: Award, value: 40, accent: '#d9a441' },
] as const;

const CHAPTERS = ['Childhood', 'School years', 'Career', 'Legacy'];

/** Fictional preview only. Identity: Sophie Lin (see @/lib/demo-content). No real user data. */
export default function IndividualLivingRecordPreview() {
  return (
    <div className="relative min-w-0">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#12372c] text-sm font-bold text-[#98f2d3]">
          SL
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-slate-900">
            Sophie Lin&apos;s Living Record
          </p>
          <p className="truncate text-xs text-slate-500">Newcomer journey builder</p>
        </div>
        <Hotspot
          label="View Journey"
          note="The Journey Map plots each memory, milestone and place in the order life happened."
          className="ml-auto"
        />
      </div>

      <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-5">
        {DIMENSIONS.map((dim) => {
          const Icon = dim.icon;
          return (
            <div
              key={dim.label}
              className="rounded-[9px] border border-slate-200 bg-white p-2.5 text-center"
            >
              <span
                className="mx-auto flex h-7 w-7 items-center justify-center rounded-full"
                style={{ background: `${dim.accent}1f`, color: dim.accent }}
              >
                <Icon size={14} aria-hidden="true" />
              </span>
              <p className="mt-1.5 truncate text-[10px] font-bold uppercase tracking-wide text-slate-500">
                {dim.label}
              </p>
              <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${dim.value}%`, background: dim.accent }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4">
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Chapters</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {CHAPTERS.map((chapter) => (
            <span
              key={chapter}
              className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700"
            >
              {chapter}
            </span>
          ))}
        </div>
      </div>

      <div className="relative mt-4 rounded-[9px] border border-[#1d9e75]/30 bg-[#eef8f4] p-3">
        <p className="text-[11px] font-bold uppercase tracking-wide text-[#0f6e56]">
          Meaningful next action
        </p>
        <p className="mt-1 text-sm leading-6 text-slate-700">
          Add a witness to &ldquo;Finished my first marathon&rdquo; to strengthen this achievement.
        </p>
        <Hotspot
          label="Open Story"
          note="Turning a memory into a story adds dates, people and context so it can be witnessed and preserved."
          className="absolute -bottom-3 right-3"
        />
      </div>
    </div>
  );
}
